// Libs
import React, { Suspense, useContext } from 'react';
import { Switch, Route } from 'react-router-dom';
// Context
import { ElementViewBuilderContext } from 'core/context';
// Hooks
import useTheme from 'hooks/useTheme';
// Components
import { Drawer } from 'components';
// Config
import ElementViewBuilderConfig from './elementView.config';

export const resolveElementViewDrawerConfig = ({
	payload = {},
	elementViewConfig = {},
	isMobile,
}) => {
	const drawerConfig = payload.drawerConfig || {};

	return {
		maxWidth:
			drawerConfig.maxWidth || (isMobile ? '100%' : elementViewConfig.maxWidth),
		fullWidthBelow:
			drawerConfig.fullWidthBelow !== undefined
				? drawerConfig.fullWidthBelow
				: elementViewConfig.fullWidthBelow,
		noOverflow:
			drawerConfig.noOverflow !== undefined
				? drawerConfig.noOverflow
				: elementViewConfig.noOverflow,
	};
};

export const ElementViewLocalSwitcher = ({ isMobile }) => {
	const { theme, isDarkTheme } = useTheme();
	const elementViewContext = useContext(ElementViewBuilderContext);
	const { stack = [], cleanElementView } = elementViewContext;

	if (stack.length === 0) return null;

	return stack.map(payload => {
		const ElementViewComponent =
			ElementViewBuilderConfig.getAuthorizedElementViewReactNode(
				payload.elementViewKey,
			);

		if (!ElementViewComponent) return null;

		const elementViewConfig =
			ElementViewBuilderConfig.getAuthorizedElementViewConfig(
				payload.elementViewKey,
			);
		const drawerConfig = resolveElementViewDrawerConfig({
			payload,
			elementViewConfig,
			isMobile,
		});

		return (
			<ElementViewBuilderContext.Provider
				key={payload.__viewId}
				value={{
					...elementViewContext,
					payload,
					cleanElementView: () => cleanElementView(payload.__viewId),
				}}
			>
				<Drawer
					className='animated fadeIn faster'
					height='100%'
					width='100%'
					backgroundColor={!isDarkTheme && theme._secondaryBackgroundColor}
					$maxWidth={drawerConfig.maxWidth}
					$fullWidthBelow={drawerConfig.fullWidthBelow}
					$noOverflow={drawerConfig.noOverflow}
					placement={elementViewConfig.placement || 'right'}
					closable={false}
					onClose={() => cleanElementView(payload.__viewId)}
					open={true}
				>
					<Suspense fallback={null}>
						<ElementViewComponent isMobile={isMobile} />
					</Suspense>
				</Drawer>
			</ElementViewBuilderContext.Provider>
		);
	});
};

const ElementViewPathSwitcher = ({ isMobile }) => {
	const elementViews =
		ElementViewBuilderConfig.getAuthorizedElementViewReactNodes();

	return (
		<Switch>
			{Object.keys(elementViews).map(path => {
				const ElementViewComponent = elementViews[path];
				if (!ElementViewComponent) return null;

				return (
					<Route
						exact
						key={path}
						path={path}
						children={<ElementViewComponent isMobile={isMobile} />}
					/>
				);
			})}
		</Switch>
	);
};

const ElementViewBuilder = ({ shortcutPath, isMobile, elementKey }) => {
	if (shortcutPath) return <ElementViewPathSwitcher isMobile={isMobile} />;
	return <ElementViewLocalSwitcher isMobile={isMobile} />;
};

export default ElementViewBuilder;
